import { ProductVariant } from '../shopify/types';

/**
 * Check if a requested quantity exceeds the available inventory
 * @param variant The product variant
 * @param requestedQuantity The quantity being requested
 * @param currentQuantity Optional current quantity in cart
 * @returns Object with validation result and error message
 */
export function validateInventory(
  variant: ProductVariant | undefined,
  requestedQuantity: number,
  currentQuantity: number = 0
): { valid: boolean; message?: string; availableToAdd: number } {
  // If no variant or no quantity available info, assume it's valid
  if (!variant || variant.quantityAvailable === undefined) {
    return { valid: true, availableToAdd: requestedQuantity };
  }

  const totalRequested = currentQuantity + requestedQuantity;
  const isValid = totalRequested <= variant.quantityAvailable;
  const availableToAdd = Math.max(0, variant.quantityAvailable - currentQuantity);

  if (!isValid) {
    const message = `Only ${variant.quantityAvailable} item${variant.quantityAvailable !== 1 ? 's' : ''} available, and ${currentQuantity} already in cart`;
    return { valid: false, message, availableToAdd };
  }

  return { valid: true, availableToAdd };
}

/**
 * Format stock availability message based on quantity
 * @param quantityAvailable Available stock quantity
 * @returns Formatted stock message
 */
export function formatStockMessage(quantityAvailable: number | undefined): string {
  if (quantityAvailable === undefined) return '';
  if (quantityAvailable === 0) return 'Out of stock';
  if (quantityAvailable <= 5) return `Only ${quantityAvailable} left in stock`;
  return `${quantityAvailable} in stock`;
}

/**
 * Get CSS class for stock text based on availability
 * @param quantityAvailable Available stock quantity
 * @returns CSS class name
 */
export function getStockTextClass(quantityAvailable: number | undefined): string {
  if (quantityAvailable === undefined) return '';
  if (quantityAvailable === 0) return 'text-red-500';
  if (quantityAvailable <= 5) return 'text-amber-600';
  return 'text-green-600';
}

/**
 * Get inventory information for a product variant
 * @param productId Product ID (gid)
 * @param variantId Variant ID (gid)
 * @returns Promise with inventory data
 */
export async function fetchVariantInventory(
  productId: string,
  variantId: string
): Promise<{
  quantityAvailable?: number;
  availableForSale: boolean;
  error?: string;
}> {
  try {
    // Validate input parameters
    if (!productId || !variantId) {
      console.error('Missing required parameters for fetchVariantInventory', {
        productId,
        variantId
      });
      return {
        availableForSale: true,
        error: 'Missing required product or variant ID'
      };
    }

    // Check that product ID doesn't contain "ProductVariant" (basic validation)
    if (productId.includes('ProductVariant')) {
      console.error('Invalid product ID format in fetchVariantInventory', { productId });
      return {
        availableForSale: true,
        error: 'Invalid product ID format'
      };
    }

    // Properly encode the parameters to avoid URL parsing issues
    const encodedProductId = encodeURIComponent(productId);
    const encodedVariantId = encodeURIComponent(variantId);

    console.log(`Fetching inventory: Product ID=${productId}, Variant ID=${variantId}`);

    const response = await fetch(
      `/api/products/inventory?id=${encodedProductId}&variantId=${encodedVariantId}`
    );

    if (response.ok) {
      const data = await response.json();
      // Handle the response structure consistently
      return {
        quantityAvailable: data.quantityAvailable,
        availableForSale: data.availableForSale || true
      };
    } else {
      // Extract error details from the response
      let errorMessage = `API error: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (parseError) {
        // Unable to parse JSON error response
      }

      console.error(`Inventory API error: ${errorMessage}`, {
        productId,
        variantId,
        status: response.status
      });

      return {
        availableForSale: true,
        error: errorMessage
      };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('Error fetching inventory:', errorMessage, { productId, variantId });
    return {
      availableForSale: true,
      error: errorMessage
    };
  }
}
